
/**
 * Resizes an image file to a maximum width/height while maintaining aspect ratio.
 * Converts to JPEG with specified quality.
 */
export const resizeImage = (
  file: File, 
  maxWidth = 800, 
  maxHeight = 800, 
  quality = 0.8
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        
        // Draw to canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Image conversion failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = (err) => reject(err);
    };
    
    reader.onerror = (err) => reject(err);
  });
};

/**
 * Normalizes a phone number to a standard format.
 * - Removes non-digit characters (except +)
 * - Handles Irish mobile prefixes (08x)
 * - Ensures consistent spacing for display (e.g. "087 123 4567")
 */
export const formatPhoneNumber = (input: string): string => {
  if (!input) return '';
  
  // Remove all non-digit characters except +
  let cleaned = input.replace(/[^\d+]/g, '');
  
  // Handle international prefix for Ireland (+353)
  if (cleaned.startsWith('+353')) {
    cleaned = '0' + cleaned.substring(4);
  } else if (cleaned.startsWith('00353')) {
    cleaned = '0' + cleaned.substring(5);
  }
  
  // Format based on length and prefix
  // Irish Mobile: 08x xxx xxxx (10 digits)
  if (/^08\d{8}$/.test(cleaned)) {
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6)}`;
  }
  
  // Irish Landline (Dublin): 01 xxx xxxx (9 digits)
  if (/^01\d{7}$/.test(cleaned)) {
    return `${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5)}`;
  }
  
  // Irish Landline (Other): 0xx xxx xxxx (mostly 9-10 digits)
  if (/^0\d{8,9}$/.test(cleaned)) {
    // Guess generic split: Area code (3) - Number
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3)}`;
  }
  
  // Default: Return cleaned number if no specific format matches
  // or return original if it's very short/non-standard
  return cleaned.length > 5 ? cleaned : input;
};

/**
 * Validates if a string looks like a valid phone number
 */
export const isValidPhoneNumber = (input: string): boolean => {
  // Allow spaces, dashes, parentheses, +, and digits
  const cleaned = input.replace(/[\s\-\(\)\.]/g, '');
  
  // Basic check: must have at least 7 digits, max 15
  // Optional + at start
  return /^\+?\d{7,15}$/.test(cleaned);
};
